import { inject, Injectable } from '@angular/core';
import { DomAccessor } from './dom-accessor';

export type UsercentricsServiceConsent = Readonly<{
  id: string;
  categorySlug: string;
  consentStatus: boolean | undefined;
}>;

type UsercentricsServiceResponse = {
  id?: unknown;
  categorySlug?: unknown;
  consent?: {
    status?: unknown;
  };
};

type UsercentricsUi = {
  isInitialized?: () => boolean;
  getServicesBaseInfo?: () => unknown;
  showSecondLayer?: () => void;
};

/**
 * Narrow, guarded boundary around the Usercentrics browser global.
 */
@Injectable({
  providedIn: 'root',
})
export class UsercentricsBrowserService {
  readonly #domAccessor = inject(DomAccessor);

  readServiceConsents(): readonly UsercentricsServiceConsent[] | undefined {
    const services = this.#readServices();
    if (!services) {
      return undefined;
    }

    return services
      .map(service => this.#normalizeService(service))
      .filter((service): service is UsercentricsServiceConsent => service !== undefined);
  }

  openPrivacySettings(): void {
    const usercentricsUi = this.#usercentricsUi();
    if (!usercentricsUi || typeof usercentricsUi.showSecondLayer !== 'function') {
      return;
    }

    try {
      usercentricsUi.showSecondLayer();
    } catch {
      // The CMP is externally loaded; opening its UI must not break the page.
    }
  }

  #usercentricsUi(): UsercentricsUi | undefined {
    const browserWindow = this.#domAccessor.window;
    if (!browserWindow) {
      return undefined;
    }

    const candidate = (browserWindow as Window & { UC_UI?: unknown }).UC_UI;
    return candidate && typeof candidate === 'object' ? candidate as UsercentricsUi : undefined;
  }

  #readServices(): readonly unknown[] | undefined {
    const usercentricsUi = this.#usercentricsUi();
    if (!usercentricsUi || typeof usercentricsUi.isInitialized !== 'function' || typeof usercentricsUi.getServicesBaseInfo !== 'function') {
      return undefined;
    }

    try {
      if (!usercentricsUi.isInitialized()) {
        return undefined;
      }

      const services = usercentricsUi.getServicesBaseInfo();
      return Array.isArray(services) ? services : undefined;
    } catch {
      return undefined;
    }
  }

  #normalizeService(service: unknown): UsercentricsServiceConsent | undefined {
    if (!service || typeof service !== 'object') {
      return undefined;
    }

    const candidate = service as UsercentricsServiceResponse;
    if (typeof candidate.id !== 'string' || typeof candidate.categorySlug !== 'string') {
      return undefined;
    }

    return {
      id: candidate.id,
      categorySlug: candidate.categorySlug,
      consentStatus: typeof candidate.consent?.status === 'boolean'
        ? candidate.consent.status
        : undefined,
    };
  }
}
